// Central content + media config. Videos are local placeholders — swap `src`
// values with real client clips (e.g. from the supplied Drive) any time.

export const V = {
  faceless: "/assets/real-faceless.mp4",
  motion1: "/assets/real-motion1.mp4",
  motion2: "/assets/real-motion2.mp4",
  talking: "/assets/real-talking.mp4",
  vsl: "/assets/real-vsl.mp4",
};

export const LOGO = "/assets/logo.png";
export const CALENDLY = "https://calendly.com/akashdeepsen001/30min";

export const NAV = [
  { label: "WORK", href: "#work" },
  { label: "CASE STUDIES", href: "#cases" },
  { label: "SYSTEM", href: "#system" },
  { label: "ABOUT", href: "#about" },
  { label: "CONTACT", href: "#contact" },
];

export const SHOWREEL = [V.motion1, V.talking, V.vsl, V.faceless, V.motion2, V.motion1];

export const SELECTED_WORK = [
  {
    id: "ramin",
    client: "RAMIN POPAL",
    category: "PERSONAL BRAND / SHORT-FORM",
    year: "2025",
    result: "200K → 450K+",
    video: V.faceless,
  },
  {
    id: "jack",
    client: "JACK ROBSON",
    category: "ORGBUILD / POST-PRODUCTION",
    year: "2025",
    result: "5 CLIENTS / 14 DAYS",
    video: V.talking,
  },
  {
    id: "jorn",
    client: "JORN KIRKELS",
    category: "CELLULAR / CONVERSION CONTENT",
    year: "2025",
    result: "€50K+ MRR",
    video: V.vsl,
  },
  {
    id: "eilish",
    client: "EILISH FLESCH",
    category: "EILISH BRANDS / VSL & WEB CREATIVE",
    year: "2025",
    result: "WEBSITE CONVERSION",
    video: V.motion2,
  },
];

export const CASE_STUDIES = [
  {
    id: "jorn",
    index: "01",
    client: "JORN KIRKELS",
    company: "CELLULAR",
    tagline: "Post-production built to sell a flagship program.",
    metric: "€50K+",
    metricLabel: "MONTHLY RECURRING REVENUE",
    video: V.vsl,
    challenge:
      "A flagship program with real substance, but content that never carried its weight into the sale. Strong offer, quiet distribution.",
    strategy:
      "We rebuilt the post-production language around a single job: move attention into intent. Every cut engineered to protect retention and hand the viewer to the offer.",
    execution:
      "A recurring library of conversion-first creatives — hooks tested against watch-through, pacing tuned per platform, and edits sequenced to the buyer journey.",
    result:
      "Content became the primary sales engine for the program, compounding into €50,000+ in monthly recurring revenue.",
  },
  {
    id: "ramin",
    index: "02",
    client: "RAMIN POPAL",
    company: "PERSONAL BRAND",
    tagline: "Volume and precision across every social surface.",
    metric: "300+",
    metricLabel: "NEW SALES IN ONE MONTH",
    video: V.faceless,
    challenge:
      "A personal brand ready to scale, held back by output. Not enough content, and not enough of it engineered to convert.",
    strategy:
      "We designed a high-volume short-form system — repeatable formats, a hook bank, and a distribution rhythm that kept the feed relentless without diluting quality.",
    execution:
      "Dozens of platform-native edits shipped across socials, each one built on a proven retention structure and a clear call to action.",
    result:
      "Audience scaled from 200K to 450K+, and the content pipeline drove roughly 300 new sales in a single month.",
  },
  {
    id: "jack",
    index: "03",
    client: "JACK ROBSON",
    company: "ORGBUILD",
    tagline: "Post-production that opens doors.",
    metric: "5",
    metricLabel: "NEW CLIENTS IN 14 DAYS",
    video: V.talking,
    challenge:
      "A B2B company sitting on strong results with no content system to prove it to the right buyers.",
    strategy:
      "We turned expertise into evidence — editing raw material into sharp, credibility-first creatives designed to land with decision makers.",
    execution:
      "A focused run of post-production videos for OrgBuild, structured around proof, clarity and a direct path to conversation.",
    result:
      "Five new clients closed within fourteen days of the content going live.",
  },
  {
    id: "eilish",
    index: "04",
    client: "EILISH FLESCH",
    company: "EILISH BRANDS",
    tagline: "Website creative that does the selling.",
    metric: "VSL",
    metricLabel: "+ Q&A WEBSITE SYSTEM",
    video: V.motion2,
    challenge:
      "A brand with a strong offer and a website that explained instead of persuaded.",
    strategy:
      "We treated the site as a stage. A VSL to carry the pitch, and Q&A videos to dissolve objections exactly where they surface.",
    execution:
      "A complete suite of website creatives — video sales letter and supporting Q&A pieces — produced and placed to guide visitors toward action.",
    result:
      "A website that moves the way the offer deserves: clear, cinematic and built to convert.",
  },
];

export const SYSTEM_STEPS = ["ATTENTION", "RETENTION", "TRUST", "CONVERSION", "REVENUE"];

export const SERVICES = [
  { n: "01", title: "CONTENT ARCHITECTURE", video: V.motion1, desc: "The structure beneath everything — formats, hooks and narrative systems, defined before a single frame is cut." },
  { n: "02", title: "SHORT-FORM PRODUCTION", video: V.faceless, desc: "Platform-native edits engineered for attention and watch-through, produced at volume without losing precision." },
  { n: "03", title: "PERSONAL BRAND SYSTEMS", video: V.talking, desc: "A repeatable engine for founders and creators — presence that compounds, not one-off posts." },
  { n: "04", title: "DISTRIBUTION", video: V.motion2, desc: "The right cut, on the right surface, at the right cadence. Reach as a designed outcome." },
  { n: "05", title: "CONVERSION CONTENT", video: V.vsl, desc: "VSLs, sales edits and objection-killers built to turn attention into intent." },
  { n: "06", title: "CONTENT REVENUE SYSTEMS", video: V.motion1, desc: "Where it all resolves — content wired directly to pipeline, sales and recurring revenue." },
];

export const BREAKDOWN_LABELS = [
  { at: 0.06, label: "HOOK" },
  { at: 0.26, label: "PATTERN INTERRUPT" },
  { at: 0.46, label: "RETENTION" },
  { at: 0.62, label: "SOCIAL PROOF" },
  { at: 0.8, label: "CTA" },
  { at: 0.93, label: "CONVERSION" },
];

export const TESTIMONIALS = [
  {
    quote:
      "The turnaround, communication and quality completely changed how we operate. Content stopped being a bottleneck and started being our edge.",
    accent: ["quality", "edge."],
    name: "JORN KIRKELS",
    role: "FOUNDER / CELLULAR",
  },
  {
    quote:
      "They understood the offer better than we did — and the edits proved it. Every cut was built to sell, not just to look good.",
    accent: ["offer", "sell,"],
    name: "JACK ROBSON",
    role: "FOUNDER / ORGBUILD",
  },
  {
    quote:
      "Our website finally sells the way the brand deserves. The VSL and Q&A pieces do the convincing before we ever get on a call.",
    accent: ["sells", "convincing"],
    name: "EILISH FLESCH",
    role: "FOUNDER / EILISH BRANDS",
  },
  {
    quote:
      "The volume never came at the cost of quality. Consistent, platform-native output — and it just kept converting.",
    accent: ["volume", "converting."],
    name: "RAMIN POPAL",
    role: "CREATOR",
  },
  {
    quote:
      "What sets them apart is the thinking behind the edit. Hooks, pacing, retention — nothing is left to chance.",
    accent: ["thinking", "retention"],
    name: "DANIEL VOSS",
    role: "HEAD OF GROWTH / NORTHBOUND",
  },
  {
    quote:
      "Reliable, fast and genuinely invested in the outcome. It feels less like a vendor and more like an in-house team.",
    accent: ["invested", "in-house"],
    name: "SOFIA MARÉ",
    role: "FOUNDER / MARÉ STUDIO",
  },
];

export const METRICS = [
  { value: "200K", suffix: "→ 450K+", label: "AUDIENCE GROWTH" },
  { value: "372", suffix: "+", label: "VIDEOS PRODUCED" },
  { value: "28M", suffix: "+", label: "TOTAL VIEWS" },
  { value: "300", suffix: "+", label: "SALES GENERATED" },
];
